import React, { Fragment, useEffect, useMemo, useState } from 'react';

import { Typography, styled } from '@mui/material';
import { useAuthContext } from 'src/auth/useAuthContext';
import useUIState from 'src/state/ui/persistUiState';
import { ImageColorPresets } from './constants';
import { useImageUpload } from './useImageUpload';

type Props = {
  categories: { title: string; valueName: string }[];
  handleImageSelect: (value: string, type: 'color' | 'custom') => void;
};

const StyledImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '100%',
  borderRadius: theme.spacing(1),
}));

const StyledDiv = styled('div')(({ theme }) => ({
  height: 60,
  transition: theme.transitions.create(['box-shadow']),
  overflow: 'hidden',
  borderRadius: theme.spacing(1),
  '&:hover': {
    cursor: 'pointer',
    boxShadow: theme.shadows[4],
  },
  [theme.breakpoints.up('sm')]: {
    width: 120,
    margin: theme.spacing(0.5),
  },
}));

const StyledContainer = styled('div')`
  font-family: 'Nunito', 'helvetica', sans-serif, 'noto color emoji';
`;

const StyledCoverContainer = styled('div')(({ theme }) => ({
  display: 'grid',
  gap: theme.spacing(1),
  gridTemplateColumns: 'repeat(3, 1fr)',
  gridTemplateRows: '60px',
  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
  },
}));

export const ImagePresets: React.FC<Props> = ({
  handleImageSelect,
  categories,
}) => {
  const { uploadedURLs } = useUIState();
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const { getImages } = useImageUpload();
  const { user } = useAuthContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    getImages(user?.drive?.backgroundsFolderId || '').then((images) => {
      setUploadedImages(images.map((img) => img.url) as string[]);
    });
  }, []);

  const renderContent = useMemo(
    () => (
      <>
        {!!uploadedURLs?.length && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
              My Image Url's
            </Typography>
            <StyledCoverContainer>
              {uploadedURLs?.map((url, i) => (
                <StyledDiv
                  key={i}
                  onClick={() => handleImageSelect(url, 'custom')}
                >
                  <StyledImage src={url} alt="cover" />
                </StyledDiv>
              ))}
            </StyledCoverContainer>
          </>
        )}

        {!!uploadedImages?.length && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
              My Uploaded Images
            </Typography>
            <StyledCoverContainer>
              {uploadedImages?.map((image, i) => (
                <StyledDiv
                  key={i}
                  onClick={() => handleImageSelect(image, 'custom')}
                >
                  <StyledImage src={image} alt="cover" />
                </StyledDiv>
              ))}
            </StyledCoverContainer>
          </>
        )}

        {categories.map(({ title, valueName: category }) => (
          <Fragment key={category}>
            <Typography key={title} variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
              {title}
            </Typography>
            <StyledCoverContainer key={`${title}_container`}>
              {(ImageColorPresets as Record<any, string[]>)[category].map(
                (value, i) =>
                  ['colors', 'gradients'].includes(category) ? (
                    <StyledDiv
                      key={i}
                      style={{ background: value }}
                      onClick={() => handleImageSelect(value, 'color')}
                    />
                  ) : (
                    <StyledDiv
                      key={i}
                      onClick={() => handleImageSelect(value, 'custom')}
                    >
                      <StyledImage src={value} alt="cover" />
                    </StyledDiv>
                  ),
              )}
            </StyledCoverContainer>
          </Fragment>
        ))}
      </>
    ),
    [categories, handleImageSelect, uploadedURLs, uploadedImages],
  );

  return <StyledContainer>{renderContent}</StyledContainer>;
};
